<template>
  <div ref="tabsContainer" class="tabs is-centered no-print" :class="{'ios-fix' : isTabFix}">
    <ul :class="{'fixed-position' : isTabFix}">
      <li v-for="tab in displayedTabs" :key="tab.name" @click="select(tab.name)" :class="{'is-active': currentTab === tab.name}">
        <a v-if="canShow(tab)" :class="'gtm-profile-'+ tab.label.toLowerCase() +'-tab'">
          <span v-if="tab.image"><img class="tab-selector-image" :src="tab.image"></span>
          <span class="tab-selector-title">{{displayLabel(tab)}}</span>
          <sup v-if="tab.countKey && counts[tab.countKey]"
               class="submenu-count " :class="'gtm-profile-'+ tab.label.toLowerCase() +'-tab'">
            {{counts[tab.countKey]}}
          </sup>
        </a>
      </li>
    </ul>
    <modal-custom v-if="showDiscardChangesModal"
      :hasFooter="true" :title="'Discard unsaved changes'" :text="'Navigating away will discard your unsaved changes. Are you sure you want to continue?'"
      :successText="'Discard'" :cancelText="'Cancel'" @modalSuccess="handleDiscard()"
      @modalCancel="showDiscardChangesModal = false">
    </modal-custom>
  </div>
</template>

<script>
import state from './state'
import ModalCustom from '@/components/Modals/Custom'

import breakpoints from '@/libs/breakpoints'

import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'
import userTypes from '@/mixins/user-types'

import i18n from '@/i18n'

export default {
  components: {
    ModalCustom
  },
  mixins: [auth, componentState, userTypes],
  props: ['user', 'profile'],
  data: function () {
    return {
      tabs: [
        { name: 'TabStoryBoards', label: 'Stories', countKey: 'stories', jsonStr: 'sectionProfile.stories' },
        { name: 'TabProjectBoards', label: 'Projects', countKey: 'projects', jsonStr: 'sectionProfile.projects' },
        { name: 'TabContributions', label: 'Contributions', countKey: 'contributions', jsonStr: 'sectionProfile.contributions' },
        { name: 'TabMyClasses', label: 'MyClasses', countKey: 'myClasses', jsonStr: 'sectionProfile.myClasses' },
        { name: 'TabPins', label: 'Pins', countKey: 'pins', jsonStr: 'sectionProfile.pins' },
        { name: 'TabFollowing', label: 'Following', countKey: 'following', jsonStr: 'sectionProfile.following' },
        { name: 'TabFollowers', label: 'Followers', countKey: 'followers', jsonStr: 'sectionProfile.followers' },
        { name: 'TabAbout', label: 'About', jsonStr: 'sectionProfile.about' },
        { name: 'TabPersonalInformation', label: 'Personal Information', jsonStr: 'sectionProfile.personalInformation', image: '/static/big-lock.png' },
        { name: 'TabSettings', label: 'Settings', editableKey: 'settings', jsonStr: 'sectionProfile.settings' }
      ],
      isTabFix: false,
      showDiscardChangesModal: false,
      savedTabSelection: null,
      showManageClassTab: false
    }
  },
  watch: {
    // used to update this Profile component when the dropdown menu changes the URL parameter
    '$route' (to, from) {
      if (to !== from) {
        if (this.$route.query.activeTab) {
          this.select(this.$route.query.activeTab)
        }
      }
    },
    showPinTab: function () {
      if (this.$route.params.tab && this.$route.params.tab === 'pins' && this.showPinTab) {
        this._set(state.tab.current, 'TabPins')
      }
    }
  },
  computed: {
    currentTab: function () {
      let defaultTab = state.tab.default
      if (this.$isEngCom) {
        if (!this.user.isEngStaff) {
          defaultTab = state.tab.defaultCantPublish
        } else {
          defaultTab = state.tab.defaultStories
        }
      }
      if ((this.$isYSC || this.$isOhioScience) && this.showManageClassTab) {
        defaultTab = state.tab.manageClassTab
      }
      return this._get(state.tab.current, defaultTab)
    },
    counts: function () {
      return this.profile ? this.profile.counts || {} : {}
    },
    isMobile: function () {
      return breakpoints.isMobile()
    },
    viewingOwnProfile: function () {
      return this.user.id === this.currentUser.id
    },
    currentUserPin: function () {
      return this.$store.getters.currentUserPin
    },
    viewedUserPin: function () {
      return this.$store.getters.pinByUsername(this.user.userName)
    },
    appName: function () {
      return process.env.APP_NAME.toLowerCase()
    },
    displayedTabs: function () {
      return this.tabs.filter(tab => {
        if (tab.name === 'TabMyClasses' && this.appName === 'projectboard') {
          return
        }
        if (this.currentUser.type.includes(this.UserTypeTeacher) && this.$route.params.username && tab.name === 'TabMyClasses') {
          return
        }
        if (!this.currentUser.type.includes(this.UserTypeTeacher) && tab.name === 'TabMyClasses') {
          return
        }
        if (!this.showPersonalInformationTab && tab.name === 'TabPersonalInformation') {
          return
        }

        if (this.$isEngCom) {
          if (tab.name === 'TabProjectBoards') {
            return
          }
        } else {
          if (tab.name === 'TabStoryBoards') {
            return
          }
        }

        if (!this.showPinTab && tab.name === 'TabPins') {
          return
        }
        return tab
      })
    },
    showPinTab: function () {
      // we want to see this tab on specific platforms
      // for admins or users looking at their own profiles
      // or user is looking at a profile who has collected pins
      // or for users that have a pin looking at a users profile that also has a pin
      if (!this.$isGirlsAndSteam && !this.$isIsef && !this.$isYSC && !this.$isAjas) {
        return false
      }

      if (this.profile?.counts?.pins > 0 || (this.currentUserPin && this.viewedUserPin)) {
        return true
      }

      return (this.user.canSwapPins || this.isAdmin) && this.viewingOwnProfile
    },
    showPersonalInformationTab: function () {
      return this.$isEngCom && this.viewingOwnProfile
    }
  },
  mounted: function () {
    window.addEventListener('scroll', this.shouldFixTabBoard)
    if (this.$isEngCom && !this.user.isEngStaff) {
      this.tabs.shift()
    }

    if (this.$route.query.activeTab) {
      this._set(state.tab.current, this.$route.query.activeTab)
    }
  },
  destroyed: function () {
    window.removeEventListener('scroll', this.shouldFixTabBoard)
  },
  methods: {
    select: function (tab) {
      if (this._get(state.editingCount) > 0) {
        this.savedTabSelection = tab
        this.showDiscardChangesModal = true
      } else {
        if (this._get(state.tab.current) !== tab) {
          this.$router.replace({ query: { activeTab: tab } }).catch(failure => {
            console.log('Ignoring router link navigation failure')
          })
          this._set(state.tab.current, tab)
        }
      }
    },
    canShow: function (tab) {
      if (!tab.editableKey) {
        return true
      }
      return this._get(state.editable[tab.editableKey], false)
    },
    shouldFixTabBoard: function () {
      const rect = this.$refs.tabsContainer.getBoundingClientRect()
      this.isTabFix = rect.top < 52
    },
    handleDiscard: function () {
      this.showDiscardChangesModal = false
      this._set(state.editingCount, 0)
      this.select(this.savedTabSelection)
    },
    displayLabel: function (tab) {
      if (tab.label === 'Stories') {
        return tab.label
      }
      return i18n.t(tab.jsonStr)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

.tabs {
  margin-top:1.5rem;
  overflow-y: hidden;
  overflow-x: auto;
  @include mobile {
    margin: 0.5rem 0;
  }

  ul {
    border-bottom-width: 1px;
    transition: 0.25s all;
    a {
      position:relative;
      span{
        margin-right:1px;
        font-size: 0.9rem;
      }
    }

    @include mobile{
      font-size: 0.8em;
      border-top: 1px solid #dbdbdb;

      a {
        padding:0.5em 0.8em;
      }
    }
  }
}

.fixed-position {
  position: fixed;
  top: $navbar-height;
  right: 0;
  left: 0;
  z-index: 10;
  overflow-x: auto;
  overflow-y: hidden;
  background: rgba(242,242,242,0.9);
  @include mobile {
    justify-content: space-between !important;
  }
}

.submenu-count{
  font-size: 0.7rem;
  color: $primary;
  margin-top:-11px;
}

.tab-selector-image {
  margin: 3px 5px 0 0;
  width: 15px;
}

.tab-selector-title {
  margin-bottom: 3px;
}

.ios-fix{
  overflow:hidden;
}

</style>
